import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import * as Cesium from 'cesium';
import { X, MapPin, Loader2, ChevronDown, Search, Navigation, AlertCircle, Layers } from 'lucide-react';
import '../../../_proto/landscope-map-viewer_pb';
import { useDrawingContext } from './DrawingContext';
import { coordinateService } from '../../services/coordinateService';
import { grpcClientFactory } from '../../services/grpcClientFactory';
export const AddressSearchPanel = React.memo(({ viewer, isVisible, compact = false, coverageVisible, onToggleCoverage, coverageOpacity, onCoverageOpacityChange }) => {
    const { state, actions } = useDrawingContext();
    const { address } = state;
    // Refs for cleanup and outside click detection
    const addressSearchDebounceRef = useRef();
    const addressInputRef = useRef(null);
    const suggestionsRef = useRef(null);
    // Create gRPC client
    const addressSearchClient = useMemo(() => grpcClientFactory.createAddressSearchClient(), []);
    // Search mode state
    const [searchMode, setSearchMode] = useState('address');
    const [coordinateMode, setCoordinateMode] = useState('latlong');
    // Lat/Long state
    const [latValue, setLatValue] = useState('');
    const [lngValue, setLngValue] = useState('');
    // UTM state
    const [utmZone, setUtmZone] = useState('');
    const [utmHemisphere, setUtmHemisphere] = useState('N');
    const [utmEasting, setUtmEasting] = useState('');
    const [utmNorthing, setUtmNorthing] = useState('');
    // Coordinate UI state
    const [coordError, setCoordError] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    // Fly to coordinates helper
    const flyToCoordinates = useCallback((lat, lng) => {
        if (!viewer || viewer.isDestroyed())
            return;
        viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(lng, lat, 5000),
            duration: 3.0
        });
    }, [viewer]);
    // Handle lat/long submit
    const handleLatLongSubmit = useCallback(() => {
        setCoordError(null);
        const lat = parseFloat(latValue);
        const lng = parseFloat(lngValue);
        if (isNaN(lat) || lat < -90 || lat > 90) {
            setCoordError('Latitude must be between -90 and 90');
            return;
        }
        if (isNaN(lng) || lng < -180 || lng > 180) {
            setCoordError('Longitude must be between -180 and 180');
            return;
        }
        flyToCoordinates(lat, lng);
    }, [latValue, lngValue, flyToCoordinates]);
    // Handle UTM submit
    const handleUtmSubmit = useCallback(async () => {
        setCoordError(null);
        const zone = parseInt(utmZone, 10);
        const easting = parseFloat(utmEasting);
        const northing = parseFloat(utmNorthing);
        if (isNaN(zone) || zone < 1 || zone > 60) {
            setCoordError('UTM zone must be between 1 and 60');
            return;
        }
        if (isNaN(easting) || easting <= 0) {
            setCoordError('Easting must be a positive number');
            return;
        }
        if (isNaN(northing) || northing <= 0) {
            setCoordError('Northing must be a positive number');
            return;
        }
        setIsConverting(true);
        try {
            const result = await coordinateService.convertUTMToLatLong({
                zone,
                hemisphere: utmHemisphere,
                easting,
                northing
            });
            if (!result.success || !result.latLong) {
                setCoordError(result.error || 'UTM conversion failed');
                return;
            }
            flyToCoordinates(result.latLong.latitude, result.latLong.longitude);
        }
        catch (error) {
            setCoordError(error.message || 'UTM conversion failed');
        }
        finally {
            setIsConverting(false);
        }
    }, [utmZone, utmHemisphere, utmEasting, utmNorthing, flyToCoordinates]);
    // Handle coordinate key down (Enter to submit)
    const handleCoordinateKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (coordinateMode === 'latlong') {
                handleLatLongSubmit();
            }
            else {
                handleUtmSubmit();
            }
        }
    }, [coordinateMode, handleLatLongSubmit, handleUtmSubmit]);
    // Clear coordinate state when switching modes
    const handleSearchModeChange = useCallback((mode) => {
        setSearchMode(mode);
        setLatValue('');
        setLngValue('');
        setUtmZone('');
        setUtmHemisphere('N');
        setUtmEasting('');
        setUtmNorthing('');
        setCoordError(null);
    }, []);
    const handleCoordinateModeChange = useCallback((mode) => {
        setCoordinateMode(mode);
        setLatValue('');
        setLngValue('');
        setUtmZone('');
        setUtmHemisphere('N');
        setUtmEasting('');
        setUtmNorthing('');
        setCoordError(null);
    }, []);
    // Clear error on input change
    const clearErrorOnChange = useCallback(() => {
        if (coordError)
            setCoordError(null);
    }, [coordError]);
    // Address search function
    const searchAddresses = useCallback(async (searchText) => {
        if (searchText.length < 2) {
            actions.setAddressSuggestions([]);
            actions.showSuggestions(false);
            return;
        }
        actions.setSearching(true);
        try {
            const request = new proto.landscope.mapviewer.v1.SearchAddressRequest();
            request.setSearchText(searchText);
            const response = await new Promise((resolve, reject) => {
                addressSearchClient.searchAddress(request, {}, (err, response) => {
                    if (err) {
                        reject(err);
                    }
                    else {
                        resolve(response);
                    }
                });
            });
            const results = response.getResultsList().map((result) => ({
                name: result.getName(),
                latitude: result.getLatitude(),
                longitude: result.getLongitude()
            }));
            actions.setAddressSuggestions(results);
            actions.showSuggestions(results.length > 0);
        }
        catch (error) {
            console.error('Error searching addresses:', error);
            actions.setAddressSuggestions([]);
            actions.showSuggestions(false);
        }
        finally {
            actions.setSearching(false);
        }
    }, [addressSearchClient, actions]);
    // Debounced address search
    const debouncedAddressSearch = useCallback((searchText) => {
        // Clear existing timer
        if (addressSearchDebounceRef.current) {
            clearTimeout(addressSearchDebounceRef.current);
        }
        // Set new timer
        addressSearchDebounceRef.current = setTimeout(() => {
            searchAddresses(searchText);
        }, 300);
    }, [searchAddresses]);
    // Handle address selection
    const handleAddressSelect = useCallback((result) => {
        if (!viewer || viewer.isDestroyed()) {
            console.error('Viewer not available for map navigation');
            return;
        }
        actions.setAddress(result.name);
        actions.showSuggestions(false);
        actions.setAddressSuggestions([]);
        actions.setSelectedSuggestion(-1);
        flyToCoordinates(result.latitude, result.longitude);
    }, [viewer, actions, flyToCoordinates]);
    // Handle address input change
    const handleAddressChange = useCallback((value) => {
        actions.setAddress(value);
        actions.setSelectedSuggestion(-1);
        debouncedAddressSearch(value);
    }, [debouncedAddressSearch, actions]);
    // Handle keyboard navigation in address input
    const handleAddressKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            if (address.showSuggestions) {
                event.preventDefault();
                actions.showSuggestions(false);
                actions.setSelectedSuggestion(-1);
                return;
            }
            if (state.ui.showSearchOnMobile) {
                event.preventDefault();
                actions.toggleSearchOnMobile(false);
                return;
            }
            if (address.value) {
                event.preventDefault();
                actions.setAddress('');
                actions.setAddressSuggestions([]);
                return;
            }
            return;
        }
        if (!address.showSuggestions || address.suggestions.length === 0)
            return;
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                actions.setSelectedSuggestion(address.selectedSuggestionIndex < address.suggestions.length - 1
                    ? address.selectedSuggestionIndex + 1
                    : 0);
                break;
            case 'ArrowUp':
                event.preventDefault();
                actions.setSelectedSuggestion(address.selectedSuggestionIndex > 0
                    ? address.selectedSuggestionIndex - 1
                    : address.suggestions.length - 1);
                break;
            case 'Enter':
                event.preventDefault();
                if (address.selectedSuggestionIndex >= 0 && address.selectedSuggestionIndex < address.suggestions.length) {
                    handleAddressSelect(address.suggestions[address.selectedSuggestionIndex]);
                }
                else if (address.suggestions.length > 0) {
                    handleAddressSelect(address.suggestions[0]);
                }
                break;
        }
    }, [address.showSuggestions, address.suggestions, address.selectedSuggestionIndex, address.value, state.ui.showSearchOnMobile, handleAddressSelect, actions]);
    // Handle clear address
    const handleClearAddress = useCallback(() => {
        actions.setAddress('');
        actions.setAddressSuggestions([]);
        actions.showSuggestions(false);
        actions.setSelectedSuggestion(-1);
        if (addressSearchDebounceRef.current) {
            clearTimeout(addressSearchDebounceRef.current);
        }
    }, [actions]);
    // Hide suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            if (addressInputRef.current &&
                !addressInputRef.current.contains(target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(target)) {
                actions.showSuggestions(false);
                actions.setSelectedSuggestion(-1);
            }
        };
        if (address.showSuggestions) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [address.showSuggestions, actions]);
    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (addressSearchDebounceRef.current) {
                clearTimeout(addressSearchDebounceRef.current);
            }
        };
    }, []);
    // State for viewport width
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);
    // Toggle search visibility on mobile
    const toggleSearchVisibility = useCallback(() => {
        actions.toggleSearchOnMobile(!state.ui.showSearchOnMobile);
    }, [actions, state.ui.showSearchOnMobile]);
    if (!isVisible)
        return null;
    // Mobile search button (collapsed state) — not in compact mode, where there's
    // no address search to expand, only the coverage control.
    if (!state.ui.showSearchOnMobile && isMobile && !compact) {
        return (_jsx("div", { className: "fixed top-4 left-1/2 transform -translate-x-1/2 z-50", children: _jsx("button", { onClick: toggleSearchVisibility, className: "p-3 rounded-lg shadow-lg border flex items-center justify-center", style: { backgroundColor: 'var(--charcoal)', borderColor: 'var(--pewter)', color: 'var(--sand)' }, children: _jsx(Search, { size: 20 }) }) }));
    }
    return (_jsx("div", { className: `fixed top-4 ${isMobile ? 'left-0 right-0 px-4' : 'left-1/2 transform -translate-x-1/2 px-4 max-w-[95vw]'} z-50`, children: _jsxs("div", { className: `relative ${isMobile ? 'w-full' : 'w-full max-w-md'}`, children: [_jsxs("div", { className: "rounded-lg shadow-lg border", style: { backgroundColor: 'var(--charcoal)', borderColor: 'var(--pewter)' }, children: [!compact && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center justify-center p-2 gap-1", children: [_jsxs("div", { className: "flex rounded-full overflow-hidden border", style: { borderColor: 'var(--pewter)' }, children: [_jsx("button", { onClick: () => handleSearchModeChange('address'), className: `px-3 py-1 text-xs transition-colors ${searchMode === 'address' ? 'font-semibold' : ''}`, style: {
                                                        backgroundColor: searchMode === 'address' ? 'var(--sand)' : 'var(--charcoal)',
                                                        color: searchMode === 'address' ? 'var(--forest)' : 'var(--dew)'
                                                    }, children: "Address" }), _jsx("button", { onClick: () => handleSearchModeChange('coordinates'), className: `px-3 py-1 text-xs border-l transition-colors ${searchMode === 'coordinates' ? 'font-semibold' : ''}`, style: {
                                                        backgroundColor: searchMode === 'coordinates' ? 'var(--sand)' : 'var(--charcoal)',
                                                        color: searchMode === 'coordinates' ? 'var(--forest)' : 'var(--dew)',
                                                        borderColor: 'var(--pewter)'
                                                    }, children: "Coordinates" })] }), isMobile && (_jsx("button", { onClick: () => actions.toggleSearchOnMobile(false), className: "ml-auto p-1 rounded transition-colors hover:bg-opacity-20 hover:bg-gray-500", style: { color: 'var(--dew)' }, title: "Close search", children: _jsx(X, { size: 16 }) }))] }), _jsx("div", { className: "px-3 pb-3", children: searchMode === 'address' ? (_jsx(_Fragment, { children: _jsxs("div", { className: "flex items-center gap-2 rounded-lg px-3 py-2 border", style: { backgroundColor: 'var(--forest)', borderColor: 'var(--pewter)' }, children: [_jsx(MapPin, { size: 16, style: { color: 'var(--dew)', flexShrink: 0 } }), _jsx("input", { type: "text", value: address.value, onChange: (e) => handleAddressChange(e.target.value), onKeyDown: handleAddressKeyDown, onFocus: () => actions.showSuggestions(address.suggestions.length > 0), placeholder: "Enter address...", className: "border-none outline-none text-sm flex-1 min-w-0 bg-transparent", style: { color: 'var(--sand)' }, ref: addressInputRef, autoFocus: isMobile && state.ui.showSearchOnMobile }), address.value && (_jsx("button", { onClick: handleClearAddress, className: "transition-colors flex-shrink-0 hover:opacity-80", style: { color: 'var(--dew)' }, title: "Clear search", children: _jsx(X, { size: 14 }) })), address.isSearching && (_jsx(Loader2, { className: "animate-spin flex-shrink-0", size: 14, style: { color: 'var(--dew)' } })), !isMobile && address.showSuggestions && address.suggestions.length > 0 && (_jsx(ChevronDown, { size: 14, className: "flex-shrink-0", style: { color: 'var(--dew)' } }))] }) })) : (
                                    /* Coordinates mode */
                                    _jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs("div", { className: "flex rounded-full overflow-hidden border", style: { borderColor: 'var(--pewter)' }, children: [_jsx("button", { onClick: () => handleCoordinateModeChange('latlong'), className: `flex-1 px-3 py-1 text-xs transition-colors ${coordinateMode === 'latlong' ? 'font-semibold' : ''}`, style: {
                                                            backgroundColor: coordinateMode === 'latlong' ? 'var(--sand)' : 'var(--charcoal)',
                                                            color: coordinateMode === 'latlong' ? 'var(--forest)' : 'var(--dew)'
                                                        }, children: "Lat/Long" }), _jsx("button", { onClick: () => handleCoordinateModeChange('utm'), className: `flex-1 px-3 py-1 text-xs border-l transition-colors ${coordinateMode === 'utm' ? 'font-semibold' : ''}`, style: {
                                                            backgroundColor: coordinateMode === 'utm' ? 'var(--sand)' : 'var(--charcoal)',
                                                            color: coordinateMode === 'utm' ? 'var(--forest)' : 'var(--dew)',
                                                            borderColor: 'var(--pewter)'
                                                        }, children: "UTM" })] }), coordinateMode === 'latlong' ? (
                                            /* Lat/Long inputs */
                                            _jsxs("div", { className: "flex gap-2 items-end", children: [_jsxs("div", { className: "flex-1", children: [_jsx("label", { className: "text-xs mb-1 block", style: { color: 'var(--dew)' }, children: "Latitude" }), _jsx("input", { type: "text", inputMode: "decimal", value: latValue, onChange: (e) => { setLatValue(e.target.value); clearErrorOnChange(); }, onKeyDown: handleCoordinateKeyDown, placeholder: "-90 to 90", className: "w-full px-2 py-1.5 rounded border text-sm outline-none", style: {
                                                                    backgroundColor: 'var(--forest)',
                                                                    borderColor: coordError && coordError.includes('Latitude') ? '#ef4444' : 'var(--pewter)',
                                                                    color: 'var(--sand)'
                                                                } })] }), _jsxs("div", { className: "flex-1", children: [_jsx("label", { className: "text-xs mb-1 block", style: { color: 'var(--dew)' }, children: "Longitude" }), _jsx("input", { type: "text", inputMode: "decimal", value: lngValue, onChange: (e) => { setLngValue(e.target.value); clearErrorOnChange(); }, onKeyDown: handleCoordinateKeyDown, placeholder: "-180 to 180", className: "w-full px-2 py-1.5 rounded border text-sm outline-none", style: {
                                                                    backgroundColor: 'var(--forest)',
                                                                    borderColor: coordError && coordError.includes('Longitude') ? '#ef4444' : 'var(--pewter)',
                                                                    color: 'var(--sand)'
                                                                } })] }), _jsxs("button", { onClick: handleLatLongSubmit, className: "px-3 py-1.5 rounded text-sm font-medium transition-colors hover:opacity-90 flex items-center gap-1.5", style: { backgroundColor: 'var(--fern)', color: 'var(--sand)' }, title: "Fly to coordinates", children: [_jsx(Navigation, { size: 12 }), "Go"] })] })) : (
                                            /* UTM inputs */
                                            _jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs("div", { className: "flex gap-2 items-end", children: [_jsxs("div", { className: "flex-1", children: [_jsx("label", { className: "text-xs mb-1 block", style: { color: 'var(--dew)' }, children: "Easting" }), _jsx("input", { type: "text", inputMode: "decimal", value: utmEasting, onChange: (e) => { setUtmEasting(e.target.value); clearErrorOnChange(); }, onKeyDown: handleCoordinateKeyDown, placeholder: "Easting", className: "w-full px-2 py-1.5 rounded border text-sm outline-none", style: {
                                                                            backgroundColor: 'var(--forest)',
                                                                            borderColor: coordError && coordError.includes('Easting') ? '#ef4444' : 'var(--pewter)',
                                                                            color: 'var(--sand)'
                                                                        } })] }), _jsxs("div", { className: "flex-1", children: [_jsx("label", { className: "text-xs mb-1 block", style: { color: 'var(--dew)' }, children: "Northing" }), _jsx("input", { type: "text", inputMode: "decimal", value: utmNorthing, onChange: (e) => { setUtmNorthing(e.target.value); clearErrorOnChange(); }, onKeyDown: handleCoordinateKeyDown, placeholder: "Northing", className: "w-full px-2 py-1.5 rounded border text-sm outline-none", style: {
                                                                            backgroundColor: 'var(--forest)',
                                                                            borderColor: coordError && coordError.includes('Northing') ? '#ef4444' : 'var(--pewter)',
                                                                            color: 'var(--sand)'
                                                                        } })] })] }), _jsxs("div", { className: "flex gap-2 items-end", children: [_jsxs("div", { style: { width: '60px' }, children: [_jsx("label", { className: "text-xs mb-1 block", style: { color: 'var(--dew)' }, children: "Zone" }), _jsx("input", { type: "text", inputMode: "numeric", value: utmZone, onChange: (e) => { setUtmZone(e.target.value); clearErrorOnChange(); }, onKeyDown: handleCoordinateKeyDown, placeholder: "1-60", className: "w-full px-2 py-1.5 rounded border text-sm outline-none", style: {
                                                                            backgroundColor: 'var(--forest)',
                                                                            borderColor: coordError && coordError.includes('zone') ? '#ef4444' : 'var(--pewter)',
                                                                            color: 'var(--sand)'
                                                                        } })] }), _jsxs("div", { children: [_jsx("label", { className: "text-xs mb-1 block", style: { color: 'var(--dew)' }, children: "Hemi" }), _jsxs("div", { className: "flex rounded-full overflow-hidden border", style: { borderColor: 'var(--pewter)' }, children: [_jsx("button", { onClick: () => { setUtmHemisphere('N'); clearErrorOnChange(); }, className: "px-2 py-1.5 text-xs transition-colors", style: {
                                                                                    backgroundColor: utmHemisphere === 'N' ? 'var(--sand)' : 'var(--charcoal)',
                                                                                    color: utmHemisphere === 'N' ? 'var(--forest)' : 'var(--dew)'
                                                                                }, children: "N" }), _jsx("button", { onClick: () => { setUtmHemisphere('S'); clearErrorOnChange(); }, className: "px-2 py-1.5 text-xs border-l transition-colors", style: {
                                                                                    backgroundColor: utmHemisphere === 'S' ? 'var(--sand)' : 'var(--charcoal)',
                                                                                    color: utmHemisphere === 'S' ? 'var(--forest)' : 'var(--dew)',
                                                                                    borderColor: 'var(--pewter)'
                                                                                }, children: "S" })] })] }), _jsx("div", { className: "flex-1" }), _jsxs("button", { onClick: handleUtmSubmit, disabled: isConverting, className: "px-3 py-1.5 rounded text-sm font-medium transition-colors hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed", style: { backgroundColor: 'var(--fern)', color: 'var(--sand)' }, title: "Fly to coordinates", children: [isConverting ? (_jsx(Loader2, { className: "animate-spin", size: 12 })) : (_jsx(Navigation, { size: 12 })), "Go"] })] })] })), coordError && (_jsxs("div", { className: "flex items-center gap-1.5 text-xs", style: { color: '#ef4444' }, children: [_jsx(AlertCircle, { size: 12, className: "flex-shrink-0" }), coordError] }))] })) })] })), _jsxs("div", { className: `${compact ? '' : 'border-t'} px-3 py-2`, style: { borderColor: 'var(--pewter)' }, children: [_jsxs("button", { onClick: onToggleCoverage, className: `w-full px-3 py-1.5 text-xs flex items-center justify-center gap-1.5 rounded-full border transition-colors ${coverageVisible ? 'font-semibold' : ''}`, style: {
                                        borderColor: coverageVisible ? 'var(--forest)' : 'var(--pewter)',
                                        backgroundColor: coverageVisible ? 'var(--sand)' : 'var(--charcoal)',
                                        color: coverageVisible ? 'var(--forest)' : 'var(--dew)'
                                    }, title: coverageVisible ? 'Hide elevation-data coverage' : 'Show where elevation data is available', children: [_jsx(Layers, { size: 14 }), coverageVisible ? 'Data coverage: On' : 'Data coverage: Off'] }), coverageVisible && (_jsxs("div", { className: "flex items-center gap-2 px-1 mt-2", children: [_jsx("span", { className: "text-[10px] uppercase tracking-wide whitespace-nowrap", style: { color: 'var(--dew)' }, children: "Opacity" }), _jsx("input", { type: "range", min: 0.05, max: 0.85, step: 0.05, value: coverageOpacity, onChange: (e) => onCoverageOpacityChange(parseFloat(e.target.value)), "aria-label": "Coverage opacity", className: "flex-1 h-1 cursor-pointer accent-[var(--sand)]" }), _jsxs("span", { className: "text-[10px] tabular-nums w-8 text-right", style: { color: 'var(--sand)' }, children: [Math.round(coverageOpacity * 100), "%"] })] }))] })] }), searchMode === 'address' && address.showSuggestions && address.suggestions.length > 0 && (_jsx("div", { ref: suggestionsRef, className: "absolute z-10 mt-1 w-full rounded-lg shadow-lg max-h-60 overflow-y-auto border", style: { backgroundColor: 'var(--charcoal)', borderColor: 'var(--pewter)' }, children: address.suggestions.map((result, index) => (_jsx("div", { className: "p-3 cursor-pointer text-sm transition-colors", onClick: () => handleAddressSelect(result), style: {
                            backgroundColor: index === address.selectedSuggestionIndex ? 'var(--forest)' : 'transparent',
                            color: index === address.selectedSuggestionIndex ? 'var(--sand)' : 'var(--dew)'
                        }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = 'var(--forest)'; }, onMouseLeave: (e) => {
                            if (index !== address.selectedSuggestionIndex) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }
                        }, children: result.name }, index))) }))] }) }));
});
AddressSearchPanel.displayName = 'AddressSearchPanel';
